/* eslint-disable @atlassian/relay/graphql-naming */
import { graphql, readInlineData } from 'react-relay';
import { commitLocalUpdate, type useRelayEnvironment } from 'react-relay/hooks';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import type { optimisticUpdatesSelectedOptionsQuery$key } from '@atlassian/jira-relay/src/__generated__/optimisticUpdatesSelectedOptionsQuery.graphql';
import { JiraIssueFieldOptionAri } from '@atlassian/ari/jira/issue-field-option';
import { KNOWN_COLUMN_TYPES } from '@atlassian/jira-issue-table-common/src/constants.tsx';

const readIssuesData = (queryRef: optimisticUpdatesSelectedOptionsQuery$key | null) => {
	return readInlineData<optimisticUpdatesSelectedOptionsQuery$key>(
		graphql`
			fragment optimisticUpdatesSelectedOptionsQuery on JiraIssueConnection
			@inline
			@argumentDefinitions(
				isJscInfiniteScrollEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-jsc-infinite-scroll-enabled.relayprovider"
				}
			) {
				edges {
					fieldSets(first: 500)
						@optIn(to: "JiraSpreadsheetComponent-M1")
						@include(if: $isJscInfiniteScrollEnabled) {
						edges {
							node {
								type
								fields {
									... on JiraIssueFieldConnection {
										edges {
											node {
												__id
												__typename
												fieldId
												type
												... on JiraCheckboxesField {
													selectedOptions {
														edges {
															__id
															node {
																id
																value
															}
														}
													}
												}
												... on JiraSingleSelectField {
													fieldOption {
														optionId
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		queryRef,
	);
};

export const getSelectedOptions = (
	queryRef: optimisticUpdatesSelectedOptionsQuery$key | null,
	fieldId: string,
	type: string,
) => {
	if (!queryRef) {
		return;
	}

	const selectedOptions = readIssuesData(queryRef);
	const selectedOptionMap: { [key: string]: string } = {};

	switch (type) {
		case KNOWN_COLUMN_TYPES.CHECKBOX_SELECT:
			selectedOptions?.edges?.forEach((issue) => {
				const fieldsets = issue?.fieldSets?.edges?.filter(
					(fieldSet) => fieldSet?.node?.type === KNOWN_COLUMN_TYPES.CHECKBOX_SELECT,
				);

				fieldsets?.forEach((fieldSet) => {
					const field = fieldSet?.node?.fields?.edges?.find((f) => f?.node?.fieldId === fieldId);

					field?.node?.selectedOptions?.edges?.forEach((option) => {
						if (option?.__id != null && option?.node?.id != null) {
							const optionId: string = option?.__id ?? '';
							selectedOptionMap[optionId] = option?.node?.id;
						}
					});
				});
			});
			break;
		case KNOWN_COLUMN_TYPES.SINGLE_SELECT:
			selectedOptions?.edges?.forEach((issue) => {
				const fieldsets = issue?.fieldSets?.edges?.filter(
					(fieldSet) => fieldSet?.node?.type === KNOWN_COLUMN_TYPES.SINGLE_SELECT,
				);

				fieldsets?.forEach((fieldSet) => {
					const field = fieldSet?.node?.fields?.edges?.find((f) => {
						return f?.node?.fieldId === fieldId;
					});
					if (field?.node?.__id != null && field?.node?.fieldOption?.optionId) {
						selectedOptionMap[field?.node?.__id ?? ''] = field?.node?.fieldOption?.optionId;
					}
				});
			});
			break;
		default:
			break;
	}

	return selectedOptionMap;
};

export const optimisticUpdatesSelectedOptions = (
	environment: ReturnType<typeof useRelayEnvironment>,
	type: string,
	selectedOptionMap: {
		[key: string]: string;
	},
	options: { id: string; value: string }[],
) => {
	if (type !== KNOWN_COLUMN_TYPES.CHECKBOX_SELECT && type !== KNOWN_COLUMN_TYPES.SINGLE_SELECT) {
		return;
	}

	commitLocalUpdate(environment, (store: RecordSourceSelectorProxy) => {
		const ids = Object.keys(selectedOptionMap);
		if (ids.length > 0) {
			const optionMap: { [x: string]: string } = {};
			options.forEach((opt) => {
				optionMap[opt.id] = opt.value;
			});

			switch (type) {
				case KNOWN_COLUMN_TYPES.CHECKBOX_SELECT:
					ids.forEach((id: string) => {
						const option = store.get(id)?.getLinkedRecord('node');

						const optionId = JiraIssueFieldOptionAri.parse(selectedOptionMap[id]).optionId;
						option?.setValue(optionMap[optionId], 'value');
					});
					break;
				case KNOWN_COLUMN_TYPES.SINGLE_SELECT:
					ids.forEach((id: string) => {
						const option = store.get(id)?.getLinkedRecord('fieldOption');

						const optionId = selectedOptionMap[id];
						option?.setValue(optionMap[optionId], 'value');
					});
					break;
				default:
					break;
			}
		}
	});
};
